import config from '@/config/config';
import { del, get, patch, post, put } from '@/helpers/api.helper';
import { parseServiceResponse, ServiceResponseType } from '@/helpers/serviceParsing';
import { CartInfoType } from '@/store/guestUserCartStore';
import { useAnonymousUserStore, useUserStore } from '@/store/userStore';
import { useVendorAuthStore } from '@/store/vendorAuthStore';

const { proxy_api, mall_api } = config.api;

export async function getMallPathWithProductGroupId(productGroupId: string) {
    try {
        const response = await get(`${mall_api}/main/mall-path?productGroupId=${productGroupId}`);
        return response?.data || '';
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getMallId(path: string) {
    try {
        const response = await get(`${mall_api}/common/id/${path}`);
        return parseServiceResponse<{ mallId: string }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getMallTagList(mallId: string) {
    try {
        const response = await get(`${mall_api}/main/mall/${mallId}/tag`);
        return parseServiceResponse<Tags>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getMallProductList(mallId: string, tagName = '', offset = 0, limit = 10) {
    try {
        const response = await get(
            `${mall_api}/main/mall/${mallId}/product/tag?tagName=${tagName}&offset=${offset}&limit=${limit}`,
        );
        return parseServiceResponse<ProductListType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getMallAllSellerProduct(tagName = '', offset = 0, limit = 10) {
    try {
        const response = await get(`${mall_api}/main/mall/tag?tagName=${tagName}&offset=${offset}&limit=${limit}`);
        return parseServiceResponse<ProductListType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getMallBusinessInfo(mallId: string) {
    try {
        const response = await get(`${mall_api}/main/mall/${mallId}/business-info`);
        return parseServiceResponse<MallBusinessInfoType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getProductDetails(mallId, productId: string) {
    try {
        const response = await get(`${mall_api}/main/mall/${mallId}/product/${productId}/detail`);
        return parseServiceResponse<ProductDetailsType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function getProductOptions(mallId, productId: string) {
    try {
        const response = await get(`${mall_api}/main/mall/${mallId}/product/${productId}/options`);
        return parseServiceResponse<ProductOptionsType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function getProductOptionsV2(mallId, productId: string) {
    try {
        const response = await get(`${mall_api}/v2/main/mall/${mallId}/product/${productId}/options`);
        return parseServiceResponse<ProductOptionsResponseType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getMallInfo(mallId: string) {
    try {
        const response = await get(`${mall_api}/main/mall/${mallId}`);
        return parseServiceResponse<MallInfo>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getPinedNotification(mallId: string) {
    try {
        const response = await get(`${mall_api}/main/mall/${mallId}/info/notification`);
        return parseServiceResponse<MallNotification>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getInquiryList(mallId: string, userId: string) {
    try {
        const response = await get(`${mall_api}/main/${userId}/mall/${mallId}/inquiry`);
        return parseServiceResponse<Inquiries>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function postInquiry(userId: string, mallId: string, body: any) {
    try {
        const response = await post(`${mall_api}/main/${userId}/mall/${mallId}/inquiry`, { ...body });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getMilageList(mallId: string, userId: string) {
    try {
        const response = await get(`${mall_api}/main/${userId}/mileage/mall/${mallId}`);
        return parseServiceResponse<Mileage>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getCartList(mallId: string, userId: string) {
    try {
        const response = await get(`${mall_api}/main/${userId}/mall/${mallId}/cart`);
        return parseServiceResponse<{ cartInfo: CartInfoType }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function delCartList(mallId: string, userId: string, productIds: string[]) {
    try {
        const response = await del(`${mall_api}/main/${userId}/mall/${mallId}/cart`, { data: { productIds } });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function postProductInCart(
    mallId: string,
    userId: string,
    body: {
        representProductId: string;
        productList: { representProductId: string; productId: string; selectedCount: number }[];
    },
) {
    try {
        const response = await post(`${mall_api}/main/${userId}/mall/${mallId}/cart`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getCartListV2(mallId: string, userId: string) {
    try {
        const response = await get(`${mall_api}/v2/main/${userId}/mall/${mallId}/cart`);
        return parseServiceResponse<{ cartInfo: CartInfoType }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function delCartListV2({
    mallId,
    userId,
    cartId,
    productIds,
    setProductIds,
}: {
    mallId: string;
    userId: string;
    cartId?: string;
    productIds?: string[];
    setProductIds?: string[][];
}) {
    try {
        const response = await del(`${mall_api}/v2/main/${userId}/mall/${mallId}/cart`, {
            data: { cartId, productIds, setProductIds },
        });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function postProductInCartV2(
    mallId: string,
    userId: string,
    body: {
        productList: {
            representProductId: string;
            selectedCount: number;
            productId?: string;
            productIds?: string[];
            isSetProduct?: boolean;
            isAddonProduct?: boolean;
        }[];
    },
) {
    try {
        const response = await post(`${mall_api}/v2/main/${userId}/mall/${mallId}/cart`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function searchProduct(mallId: string, keyword: string) {
    try {
        const response = await get(`${mall_api}/main/mall/${mallId}/product/search?keyword=${keyword}`);
        return parseServiceResponse<ProductListType & { totalAmount: number }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getExtraChargeInfo(orderId: string) {
    try {
        const response = await get(`${mall_api}/main/order/${orderId}/extra-charge`);
        return parseServiceResponse<ExtraChagreType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function postProxyImg(bucketName: string, fileTypes: string, formData: any) {
    const checkEnv = import.meta.env.VITE_SET_ENV === 'DEV' ? 'DEV' : 'PRD';
    try {
        const response = await post(
            `${proxy_api}/common/files/upload/${bucketName}/${fileTypes}?env=${checkEnv}`,
            formData,
            {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            },
        );
        return parseServiceResponse<ProxyImgType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getOrderList(userId: string, limit: number, offset: number, isIssue: 'yes' | 'no' | '') {
    try {
        const response = await get(
            `${mall_api}/main/${userId}/order?limit=${limit}&offset=${offset}&isIssue=${isIssue}`,
        );
        return parseServiceResponse<UserOrderList>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getOrderListV2(userId: string, limit: number, offset: number, isIssue: boolean) {
    try {
        const response = await get(
            `${mall_api}/v2/main/${userId}/order?limit=${limit}&offset=${offset}&isIssue=${isIssue}`,
        );
        return parseServiceResponse<UserOrderList>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getOrderDetailInfo(userId: string, orderGroupId: string) {
    try {
        const response = await get(`${mall_api}/main/${userId}/order-group/${orderGroupId}`);
        return parseServiceResponse<OrderDetail>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getOrderDetailInfoV2(userId: string, orderGroupId: string) {
    try {
        const response = await get(`${mall_api}/v2/main/${userId}/order-group/${orderGroupId}`);
        return parseServiceResponse<OrderDetailV2>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getOrderCouponList(userId: string, mallId: string, orderGroupId: string) {
    try {
        const response = await get(`${mall_api}/main/${userId}/mall/${mallId}/order/${orderGroupId}`).then(
            (r) => r.data,
        );
        // return parseServiceResponse(response);
        return response;
    } catch (error) {
        throw error;
    }
}

export async function getUserCouponList(mallUserId: string) {
    try {
        const response = await get(`${mall_api}/main/${mallUserId}/coupons`);
        return parseServiceResponse<UserCouponListType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getUserCouponAvailableList({
    mallUserId,
    offset = 0,
    limit = 10,
    orderAmount = 0,
    productId = '',
    productGroupIds = [],
}: {
    mallUserId: string;
    offset?: number;
    limit?: number;
    orderAmount?: number;
    productId?: string;
    productGroupIds?: string[];
}) {
    try {
        const response = await get(
            `${mall_api}/main/${mallUserId}/coupons/available?offset=${offset}&limit=${limit}&orderAmount=${orderAmount}&productId=${productId}&productGroupIds=${productGroupIds.join(
                ',',
            )}`,
        );
        return parseServiceResponse<UserCouponListType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function downloadCoupon({
    mallUserId,
    couponIds,
    productGroupId,
}: {
    mallUserId: string;
    couponIds: string[];
    productGroupId: string;
}) {
    try {
        const response = await post(`${mall_api}/main/${mallUserId}/coupons/download`, { couponIds, productGroupId });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function requestAuthCodeForOrder(phoneNumber: string) {
    try {
        const request = await post(`${mall_api}/main/phone/sms/order/request`, { phoneNumber }).then((r) => r);
        return parseServiceResponse(request);
    } catch (err) {
        throw parseServiceResponse(err);
    }
}
export async function authenticateAuthCodeForOrder(phoneNumber: string, authCode: string) {
    try {
        const request = await post(`${mall_api}/main/phone/sms/order/authenticate`, { phoneNumber, authCode }).then(
            (r) => r,
        );
        return parseServiceResponse(request);
    } catch (err) {
        throw parseServiceResponse(err);
    }
}
export async function getNotLoginOrderWithPhoneNumber(orderNumber: string, phoneNumber: string) {
    try {
        const response = await post(`${mall_api}/main/order/${orderNumber}`, { ordererPhoneNumber: phoneNumber });
        return parseServiceResponse<OrderDetail & { accessToken: string; expiresIn: string }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function getNotLoginOrder(orderNumber: string) {
    try {
        const response = await get(`${mall_api}/main/order/${orderNumber}`);
        return parseServiceResponse<OrderDetail & { accessToken: string; expiresIn: string }>(response);
        //TODO: 비회원 인증로직 추가 시 { accessToken: string, expiresIn: string } 부분 삭제할 것.
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function getNotLoginOrderV2(orderNumber: string) {
    try {
        const response = await get(`${mall_api}/v2/main/order/${orderNumber}`);
        return parseServiceResponse<OrderDetailV2 & { accessToken: string; expiresIn: string }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getRefundInfo(userId: string, orderId: string) {
    try {
        const response = await get(`${mall_api}/main/${userId}/order/${orderId}/return-request`);
        return parseServiceResponse<RefundInfoType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function requestRefund(userId: string, orderId: string, body: any) {
    try {
        const response = await post(`${mall_api}/main/${userId}/order/${orderId}/return-request`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getInvoiceState(carriersId: string, invoiceNumber: string) {
    try {
        const response = await get(`${mall_api}/common/carriers/${carriersId}/invoice/${invoiceNumber}`);
        return parseServiceResponse<InvoiceStateType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getCarrierInfo() {
    try {
        const response = await get(`${mall_api}/common/carriers/info`);
        return parseServiceResponse<CarrierListInfo>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function createAccountByToken(accessToken: string) {
    try {
        const response = await post(
            `${mall_api}/main/mall-user-id`,
            {},
            { headers: { Authorization: `Bearer ${accessToken}` } },
        );
        return parseServiceResponse<{ mallUserId: string }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function getMallUserIdByToken(accessToken?: string) {
    const postToken = useUserStore.getState().accessToken || useAnonymousUserStore.getState().accessToken;
    try {
        const response = await get(`${mall_api}/main/mall-user-id`, {
            headers: { Authorization: `Bearer ${accessToken || postToken}` },
        });
        return parseServiceResponse<{ mallUserId: string }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getMallUserInfo(userId: string) {
    try {
        const response = await get(`${mall_api}/main/${userId}/info`);
        return parseServiceResponse<MallUserInfoType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function patchPersonalInfo(
    userId: string,
    body: {
        realName?: string;
        email?: string;
        phoneNumber?: string;
        smsMarketingConsented?: boolean;
        emailMarketingConsented?: boolean;
    },
) {
    try {
        const response = await patch(`${mall_api}/main/${userId}/info`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getShippingList(userId: string) {
    try {
        const response = await get(`${mall_api}/main/${userId}/shipping-address-book`);
        return parseServiceResponse<ShippingListType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function postShippingAddress(
    userId: string,
    addressInfo: {
        name: string;
        recipientName: string;
        postCode: string;
        recipientAddress: string;
        recipientAddressDetail: string;
        recipientPhoneNumber: string;
        isDefaultShippingAddress: boolean;
    },
) {
    try {
        const response = await post(`${mall_api}/main/${userId}/shipping-address-book`, addressInfo);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function putShippingAddress(
    userId: string,
    bookId: string,
    addressInfo: {
        name: string;
        recipientName: string;
        postCode: string;
        recipientAddress: string;
        recipientAddressDetail: string;
        recipientPhoneNumber: string;
        isDefaultShippingAddress: boolean;
    },
) {
    try {
        const response = await put(`${mall_api}/main/${userId}/shipping-address-book/${bookId}`, addressInfo);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function deleteShippingAddress(userId: string, bookId: string) {
    try {
        const response = await del(`${mall_api}/main/${userId}/shipping-address-book/${bookId}`);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function smsValidRequest(userId: string, body: { phoneNumber: string }) {
    try {
        const response = await post(`${mall_api}/main/${userId}/phone/sms/renew/request`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function smsValidAuthRequest(userId: string, body: { phoneNumber: string }) {
    try {
        const response = await post(`${mall_api}/main/${userId}/phone/sms/renew/authenticate`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function deleteOrder(userId: string, orderGroupId: string, body?: any) {
    try {
        const response = await post(`${mall_api}/main/${userId}/order-group/${orderGroupId}/refund`, { ...body });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function deleteOrderByOrderId(userId: string, orderId: string, body?: any) {
    try {
        const response = await post(`${mall_api}/main/${userId}/order/${orderId}/refund`, { ...body });
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function deleteAccountOrder(userId: string, orderGroupId: string, body: any) {
    // 가상계좌 결제인 경우 판매자가 재고 부족 등으로 인해 주문 확인 전 주문 취소를 한 다음 구매자가 환불 계좌를 입력할 때 사용하는 API
    try {
        const response = await post(`${mall_api}/main/${userId}/order-group/${orderGroupId}/refund/virtual-account`, {
            ...body,
        });
        return parseServiceResponse(response);
    } catch (err) {
        throw parseServiceResponse(err);
    }
}

export function isLogined(): boolean {
    // 소매 쇼핑몰: userStore 확인
    const { accessToken: userAccessToken, mallUserId } = useUserStore.getState();
    // 도매 쇼핑몰: vendorAuthStore 확인
    const { accessToken: vendorAccessToken } = useVendorAuthStore.getState();

    // 둘 중 하나라도 로그인되어 있으면 true
    const isUserLoggedIn = !!(userAccessToken && mallUserId);
    const isVendorLoggedIn = !!vendorAccessToken;

    return isUserLoggedIn || isVendorLoggedIn;
}

export async function getFAQData() {
    try {
        const response = await get('/data/FAQ.json');
        return parseServiceResponse<FAQListType>(response);
    } catch (error) {
        throw error;
    }
}

export async function createOrderSheet(
    userId: string,
    mallId: string,
    body: {
        productIdInfoList: {
            representProductId: string;
            productId: string;
            productAmount: number;
            ownerType: 'SELLER' | 'VENDOR';
        }[];
    },
) {
    try {
        const response = await post(`${mall_api}/main/${userId}/mall/${mallId}/order`, body);
        return parseServiceResponse<CreateOrderSheetReturnType>(response);
    } catch (error) {
        throw error;
    }
}

export async function createOrderSheetV2(
    userId: string,
    mallId: string,
    body: {
        setProducts: {
            productGroupId: string;
            productIds: string[];
            amount: number;
        }[];
        products: {
            productGroupId: string;
            productId: string;
            amount: number;
        }[];
        addonProducts: {
            productGroupId: string;
            productId: string;
            amount: number;
        }[];
        ownerType: 'SELLER' | 'VENDOR';
    },
) {
    try {
        const response = await post(`${mall_api}/v2/main/${userId}/mall/${mallId}/order`, body);
        return parseServiceResponse<CreateOrderSheetReturnType>(response);
    } catch (error) {
        throw error;
    }
}

export async function writeCompletedOrder(
    userId: string,
    mallId: string,
    body: writeCompletedOrderParameterBody,
    referrer_token?: string,
    referrer?: string,
) {
    const referrerToken = !!referrer_token ? referrer_token : '';
    try {
        const response = await post(
            `${mall_api}/main/${userId}/mall/${mallId}/order/writing-complete?referrer=${referrer || ''}`,
            body,
            {
                headers: {
                    'X-Referrer-Token': referrerToken || '',
                },
            },
        );
        return parseServiceResponse<{ mallOrderGroupId: string; amount: number }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function writeCompletedOrderV2(
    userId: string,
    mallId: string,
    body: writeCompletedOrderParameterBodyV2,
    referrer_token?: string,
    referrer?: string,
) {
    const referrerToken = !!referrer_token ? referrer_token : '';
    try {
        const response = await post(
            `${mall_api}/v2/main/${userId}/mall/${mallId}/order/writing-complete?referrer=${referrer || ''}`,
            body,
            {
                headers: {
                    'X-Referrer-Token': referrerToken || '',
                },
            },
        );
        return parseServiceResponse<{ mallOrderGroupId: string; amount: number }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function requestSmsForNewPhoneNumber(
    userId: string,
    body: {
        phoneNumber: string;
    },
) {
    try {
        const response = await post(`${mall_api}/main/${userId}/phone/sms/renew/request`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function authenticateSmsCode(
    userId: string,
    body: {
        phoneNumber: string;
        authCode: string;
    },
) {
    try {
        const response = await post(`${mall_api}/main/${userId}/phone/sms/renew/authenticate`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw error;
    }
}

export async function isIslandMountain(zipCode: string) {
    try {
        const response = await post(`${mall_api}/common/area/${zipCode}/extra-shipping-cost`, {});
        return response?.data;
    } catch (error) {
        throw error;
    }
}

export async function isJejuShippingCost(zipCode: string) {
    try {
        return await post(`${mall_api}/common/area/${zipCode}/jeju-shipping-cost`, {}).then((r) => {
            return r?.data || false;
        });
    } catch (error) {
        throw error;
    }
}

export async function getAdditionalPayResult(transactionId: string) {
    try {
        const response = await get(`${mall_api}/common/payment/pg/extra-charge/result?transactionId=${transactionId}`);
        return parseServiceResponse<AdditionalPayResultType>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function payAllInMileage(orderGroupId: string) {
    try {
        const response = await post(`${mall_api}/common/payment/full-mileage`, { orderGroupId });
        return parseServiceResponse<{ orderGroupId: string }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function payAllInShoppingPoint(orderGroupId: string) {
    try {
        const response = await post(`${mall_api}/common/payment/full-point`, { orderGroupId });
        return parseServiceResponse<{ orderGroupId: string }>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function payFailUrl(orderId: string) {
    try {
        const response = await get(`${mall_api}/common/payment/pg/fail?orderId=${orderId}&isNotCallback=true`);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function requestCancel(userId: string, orderGroupId: string, body: { reasonForCancel: string }) {
    try {
        const response = await post(`${mall_api}/main/${userId}/order-group/${orderGroupId}/cancel-request`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function requestCancelByOrderId(userId: string, orderId: string, body: { reasonForCancel: string }) {
    try {
        const response = await post(`${mall_api}/main/${userId}/order/${orderId}/cancel-request`, body);
        return parseServiceResponse(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function requestCancelWithdraw(userId: string, orderGroupId: string) {
    try {
        const request = await post(`${mall_api}/main/${userId}/order-group/${orderGroupId}/cancel-request/withdraw`);
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function requestCancelWithdrawByOrderId(userId: string, orderId: string) {
    try {
        const request = await post(`${mall_api}/main/${userId}/order/${orderId}/cancel-request/withdraw`);
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function userPurchaseComplete(userId: string, orderId: string) {
    try {
        const request = await post(`${mall_api}/main/${userId}/order/${orderId}/purchase-complete`);
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function requestRefundWithdraw(userId: string, orderId: string) {
    try {
        const request = await post(`${mall_api}/main/${userId}/order/${orderId}/return-request/withdraw`);
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function patchReturnInvoice(userId: string, orderId: string, body: any) {
    try {
        const request = await patch(`${mall_api}/main/${userId}/order/${orderId}/return/invoice`, body);
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getRefundDetail(userId: string, orderGroupId: string) {
    try {
        const response = await get(`${mall_api}/main/${userId}/order-group/${orderGroupId}/refund`);
        return parseServiceResponse<RefundOrderDetail>(response);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function getRefundDetailByOrder(userId: string, orderId: string) {
    try {
        return await get(`${mall_api}/main/${userId}/order/${orderId}/refund`).then(
            (r) => r.data?.orderRefundDetailInfo || {},
        );
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function getRefundDetailByOrderV2(userId: string, orderId: string) {
    try {
        return await get(`${mall_api}/v2/main/${userId}/order/${orderId}/refund`).then((r) => r.data?.item || {});
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function cancelVirtualAccount(userId: string, groupId: string, body: any) {
    try {
        const request = await post(`${mall_api}/main/${userId}/order-group/${groupId}/cancel/virtual-account`, {
            ...body,
        });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}
export async function cancelVirtualAccountByOrderId(userId: string, orderId: string, body: any) {
    try {
        const request = await post(`${mall_api}/main/${userId}/order/${orderId}/cancel/virtual-account`, { ...body });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

export async function refundVirtualAccount(userId: string, orderId: string, body: any) {
    try {
        const request = await post(`${mall_api}/main/${userId}/order/${orderId}/refund/virtual-account`, { ...body });
        return parseServiceResponse(request);
    } catch (error) {
        throw parseServiceResponse(error);
    }
}

// export async function getMallSalesPolicy(mallId: string, representProductId: string) {
//     try {
//         const response = await get(`${mall_api}/main/mall/${mallId}/product/${representProductId}/sales-policy`)
//         return parseServiceResponse<SalesPolicyType>(response)
//     } catch (error) { throw parseServiceResponse(error) }
// }

export async function getGroupPurchaseOrderList(userId: string, mallId: string, offset: number) {
    try {
        const response = await get(`${mall_api}/main/${userId}/mall/${mallId}/inquiry/order?offset=${offset}&limit=10`);
        return parseServiceResponse<GroupPurchaseOrderType[]>(response);
    } catch (err) {
        throw parseServiceResponse(err);
    }
}

export async function postInquiryReply(userId: string, mallId: string, inquiryId: string, body: any) {
    try {
        const request = await post(`${mall_api}/main/${userId}/mall/${mallId}/inquiry/${inquiryId}/reply`, { ...body });
        return parseServiceResponse(request);
    } catch (err) {
        throw parseServiceResponse(err);
    }
}

/* types 👇 */
/* types 👇 */
/* types 👇 */
export type FAQListType = {
    categoryList: {
        code: number;
        label: string;
    }[];
    dataList: {
        category: number;
        question: string;
        answer: string;
    }[];
};
export type MallInfo = {
    mallInfo: {
        id: string;
        name: string;
        description: string;
        mallPath: string;
        snsInfo: {
            url: string;
            sns_name: string;
        }[];
        backgroundColor: string;
        profileImage: string;
        backgroundImage: string;
        pgType: string;
        pgBusinessType: string;
    };
    status: number;
};

export type MallBusinessInfoType = {
    mallBusinessInfo: {
        companyName: string;
        ownerName: string;
        managerPhoneNumber: string;
        companyAddress: string;
        businessRegistrationNumber: string;
        mailOrderSalesRegistrationNumber: string;
        customerCenterNumber: string;
    };
};

export type MallNotification = {
    notificationList: {
        id: string;
        title: string;
        body: string;
        isPinned: boolean;
        createdAt: string;
    }[];
    status: number;
};

export type Tags = {
    tagList: {
        name: string;
        display: boolean;
    }[];
    status: number;
};

export type Inquiries = {
    // inquiryCount: number;
    inquiryList: {
        id: string;
        title: string;
        inquiryKind: string;
        body: string;
        attachedFileUrl: string[];
        createdAt: string;
        inquiryMallUserName: string;
        inquiryReplyCount: number;
        inquiryReplyInfo?: {
            messageOrder: number;
            body: string;
            attachedFileUrl: string[];
            isInquirer: boolean;
            createdAt: string;
            messageId: string;
        }[];
        mallOrderInfo: {
            orderId: string;
            orderNumber: string;
        };
        mallProductInfo: {
            productGroupId: string;
            productImageUrl: string;
            productName: string;
            productUrl: string;
        };
    }[];
    status: number;
};

export type Mileage = {
    mileageList: {
        id: string;
        mallUserId: string;
        mallId: string;
        mileageDeposit: number;
    }[];
};

export type ProductDetailsType = {
    productGroupId: string;
    representProductId: string;
    representProductName: string;
    representProductPrice: number;
    representProductImage: string;
    productImage: string[];
    discountMethod: string;
    discountValue: number;
    totalCharge: number;
    inventory: number;
    description: string;
    isForSale: boolean;
    isPublic: boolean;
    tagList: string[];
    discountPercent: number;
    productShippingPolicy: {
        shippingCost: number;
        islandMountainShippingCost: number;
        freeShippingCostBar: number;
        islandMountainDiscountShippingCostBar: number;
        islandMountainDiscountedPrice: number;
        jejuShippingCost: number;
        jejuDiscountShippingCostBar: number;
        jejuDiscountedPrice: number;
        type: 'FREE' | 'CHARGE' | 'CONDITIONAL_FREE';
        isBundle: boolean;
    };
    ownerType: 'SELLER' | 'VENDOR';
    matchingSaleDate: {
        state: 'BEFORE' | 'DURING' | 'AFTER';
        remainTime: {
            days: number;
            hours: number;
        };
        startDate: string;
        endDate: string;
        message: string;
    };
    mallCustomerCenterNumber: string;
    mallReturnAddressInfo: {
        postCode: string;
        recipientAddress: string;
        recipientAddressDetail: string;
    };
    status: number;
    vendorInfo: {
        id: string;
        name: string;
        customerCenterNumber: string;
        returnAddress: {
            postCode: string;
            recipientAddress: string;
            recipientAddressDetail: string;
        };
        returnAddressName: string;
        profileImage: string;
    } | null;
    isQuizProduct: boolean;
    availableCoupons: {
        couponId: string;
        couponCode: string;
        couponName: string;
        discountType: string;
        discountValue: number;
        minOrderAmount: number;
        maxDiscountAmount: number;
        startDate: string;
        endDate: string;
        maxUsesPerUser: number;
        scopeType: string;
        scopeValue: string;
        isDownloadable: boolean;
        issuedAt: string | null;
    }[];
};

export type ProductListType = {
    productList: {
        productGroupId: string;
        representProductId: string;
        representProductName: string;
        representProductPrice: number;
        representProductImage: string;
        productImage: string[];
        discountMethod: string;
        discountValue: number;
        totalCharge: number;
        inventory: number;
        description: string;
        isForSale: boolean;
        isPublic: boolean;
        tagList: string[];
        ownerType: 'SELLER' | 'VENDOR';
        matchingSaleDate: {
            state: 'BEFORE' | 'DURING' | 'AFTER';
            remainTime: {
                days: number;
                hours: number;
            };
            startDate: string;
            endDate: string;
            message: string;
        };
        discountPercent: number;
        productShippingPolicy: {
            shippingCost: number;
            islandMountainShippingCost: number;
            freeShippingCostBar: number;
            islandMountainDiscountShippingCostBar: number;
            islandMountainDiscountedPrice: number;
            jejuShippingCost: number;
            jejuDiscountShippingCostBar: number;
            jejuDiscountedPrice: number;
        };
        vendorInfo: {
            id: string;
            name: string;
            customerCenterNumber: string;
            returnAddress: {
                postCode: string;
                recipientAddressDetail: string;
                recipientAddress: string;
            };
        } | null;
    }[];
    totalCount: number;
    status: number;
};

export type ProductOptionsType = {
    productOptionsInfo: {
        representProductCharge: number;
        productList: {
            productId: string;
            productCharge: number;
            productPrice: number;
            inventory: number;
            options: {
                optionKey: string;
                optionValue: string;
            }[];
        }[];
    };
    status: number;
};

export type CartInfo = {
    cartInfo: {
        id: string;
        mallId: string;
        productList?: {
            productGroupId: string;
            representProductId: string;
            representProductName: string;
            representProductImage: string;
            discountMethod: string;
            discountValue: number;
            productId: string;
            productOptionsName: string;
            productPrice: number;
            productAmount: number;
            totalProductPrice: number;
        }[];
        totalAmount?: number;
        totalCharge?: number;
        shippingCost?: number;
        totalPrice: number;
    };
    status: number;
};

export type ExtraChagreType = {
    extraChargeInfo: {
        id: string;
        title: string;
        userName: string;
        extraCharge: number;
        remark: string;
        paymentStateCode: number;
    };
};

export type ProxyImgType = {
    url: string[];
    type: string[];
    imgRatio: string[];
    src_url: string[];
    src_type: string[];
    upload: string;
    msg: string;
};

export interface MallSubOrderListType {
    mallOrderId: string;
    mallOrderNumber: string;
    orderStateCode: number;
    deliveryInfo: {
        shippingMethod: string;
        carriersName: string;
        invoiceNumber: any; // TODO: string인지 number인지 확인할 것
        carriersId: string;
    };
    productInfo: {
        representProductId: string;
        representProductName: string;
        discountPercent: number;
        representProductImage: string;
        discountMethod: string;
        discountValue: number;
        productId: string;
        productOptionsName: string;
        productPrice: number;
        productAmount: number;
        shippingCost: number;
        islandMountainShippingCost: number;
        totalCharge: number;
        productGroupId: string;
    };
    isSettled: boolean;
    usedShoppingPoint: number;
}

export interface MallSubOrderListTypeV2 {
    mallOrderId: string;
    mallOrderNumber: string;
    orderState: string;
    orderStateCode: number;
    deliveryInfo: {
        shippingMethod: string;
        carriersName: string;
        invoiceNumber: string;
        carriersId: string;
    };
    productInfo: {
        representProductId: string;
        productGroupId: string;
        productName: string;
        productImage: string;
        discountMethod: string;
        discountValue: number;
        productId: string;
        productOptionsName: string;
        productPrice: number;
        productAmount: number;
        shippingCost: number;
        totalProductPrice: number;
        productOptionBundle?: {
            productId: string;
            optionName: string;
            inventory: number;
        }[];
    };
    setProductInfo: {
        representProductId: string;
        productGroupId: string;
        productName: string;
        productImage: string;
        discountMethod: string;
        discountValue: number;
        productPrice: number;
        productAmount: number;
        shippingCost: number;
        totalProductPrice: number;
        shippingPolicyId: string;
        inventory: number;
        productOptionsName?: string;
        productOptionBundle?: {
            productId: string;
            optionName: string;
            inventory: number;
        }[];
    };
    addonProductInfo: {
        productGroupId: string;
        productId: string;
        productName: string;
        productOptionsName: string;
        productPrice: number;
        productAmount: number;
        totalProductPrice: number;
    };
    isSettled: boolean;
    usedShoppingPoint: number;
}

export type UserOrder = {
    mallOrderGroupId: string;
    mallOrderGroupNumber: string;
    mallSubOrderList: MallSubOrderListType[];
    orderedAt: string;
    paymentInfo: OrderDetailPayInfoType;
    sellerInfo: {
        mallName: string;
        mallPath: string;
        ownerType: string;
        sellerComponyName: string;
        sellerEmail: string;
        sellerId: string;
        sellerName: string;
        sellerPhoneNumber: string;
    };
};

export type UserOrderV2 = {
    mallOrderGroupId: string;
    mallOrderGroupNumber: string;
    orderedAt: string;
    mallId: string;
    paymentInfo: OrderDetailPayInfoType;
    sellerInfo: {
        ownerType: string;
        sellerId: string;
        sellerName: string;
        sellerComponyName: string;
        sellerEmail: string;
        mallName: string;
        sellerPhoneNumber: string;
        mallPath: string;
    };
    mallSubOrderList: MallSubOrderListTypeV2[];
};

export type UserOrderList = {
    totalCount: number;
    orderList: UserOrder[];
};

export type UserOrderListV2 = {
    items: UserOrderV2[];
    totalCount: number;
};

interface NewType extends MallSubOrderListType {
    returnAddressInfo: {
        postCode: string;
        recipientAddress: string;
        recipientAddressDetail: string;
        customerCenterNumber: string;
        returnAddressName: string;
    };
}
export type RefundOrderDetail = {
    orderRefundDetailInfo: {
        mallOrderGroupId: string;
        mallOrderGroupNumber: string;
        mallSubOrderList: NewType[];
        shippingAddressInfo: OrderDetailShippingType;
        refundInfo: RefundOrderDetailPriceType;
        paymentInfo: OrderDetailPayInfoType;
        ordererInfo: {
            ordererEmail: string;
            ordererName: string;
            ordererPhoneNumber: string;
        };
        extraChargePaymentInfo: {
            totalCount: number;
            totalCharge: number;
        };
        orderedAt: string;
    };
};
export type RefundOrderDetailPriceType = {
    productCharge: number;
    discountCharge: number;
    shippingCost: number;
    islandMountainShippingCost: number;
    usedMileage: number;
    returnCharge: number;
};
export type OrderDetail = {
    orderDetailInfo: {
        mallOrderGroupId: string;
        mallOrderGroupNumber: string;
        mallSubOrderList: MallSubOrderListType[];
        shippingAddressInfo: OrderDetailShippingType;
        priceInfo: OrderDetailPriceType;
        paymentInfo: OrderDetailPayInfoType;
        extraChargePaymentInfo: {
            totalCount: number;
            totalCharge: number;
        };
        orderedAt: string;
        sellerInfo: {
            mallName: string;
            mallPath: string;
            ownerType: string;
            sellerComponyName: string;
            sellerEmail: string;
            sellerId: string;
            sellerName: string;
            sellerPhoneNumber: string;
        };
    };
};

export type OrderDetailV2 = {
    item: {
        mallOrderGroupId: string;
        mallOrderGroupNumber: string;
        mallSubOrderList: MallSubOrderListTypeV2[];
        shippingAddressInfo: OrderDetailShippingType;
        priceInfo: OrderDetailPriceType;
        paymentInfo: OrderDetailPayInfoType;
        extraChargePaymentInfo: {
            totalCount: number;
            totalCharge: number;
        };
        orderedAt: string;
        sellerInfo: {
            mallName: string;
            mallPath: string;
            ownerType: string;
            sellerComponyName: string;
            sellerEmail: string;
            sellerId: string;
            sellerName: string;
            sellerPhoneNumber: string;
        };
    };
};

export type OrderDetailShippingType = {
    recipientName: string;
    recipientPhoneNumber: string;
    recipientAddress: string;
    recipientAddressDetail: string;
    shippingRequestMessage: string;
    recipientPostCode: string;
    mallRequestMessage: string;
};
export type OrderDetailPriceType = {
    productCharge: number;
    discountCharge: number;
    shippingCost: number;
    islandMountainShippingCost: number;
    usedMileage: number;
    totalCharge: number;
};

export type OrderDetailPayInfoType = {
    totalCharge: number;
    paymentMethod: string;
    paymentInstallment: string;
    cashReceiptUrl: string;
    receiptUrl: string;
    usedShoppingPoint: number;
};

export type RefundInfoType = {
    returnRequestOrderInfo: {
        remark: string;
        isClosed: boolean;
        issueState: string;
        carriersName: string;
        invoiceNumber: string;
        shippingMethod: string;
        returnType: string;
    };
};

export type UserCouponListType = {
    items: {
        couponId: string;
        couponCode: string;
        couponName: string;
        couponApplyType: string;
        discountType: string;
        discountValue: number;
        minOrderAmount: number;
        maxDiscountAmount: number;
        startDate: string;
        endDate: string;
        maxUsesPerUser: number;
        scopeType: string;
        scopeValue: string;
        issuedAt: string;
        validFrom: string;
        validUntil: string;
        description: string;
        discountAmount?: number;
    }[];
};

export type InvoiceStateType = {
    from: {
        name: string;
    };
    to: {
        name: string;
    };
    state: {
        id: string;
        text: string;
    };
    progresses: {
        time: string;
        status: {
            id: string;
            text: string;
        };
        location: {
            name: string;
        };
        description: string;
    }[];
    carrier: {
        id: string;
        name: string;
        tel: string;
    };
};

export type CarrierListInfo = {
    invoiceInfo: {
        id: string;
        name: string;
        tel: string;
    }[];
};

export type MallUserInfoType = {
    id: string;
    realName: string | null;
    email: string | null;
    phoneNumber: string | null;
    smsMarketingConsented: boolean;
    emailMarketingConsented: boolean;
};

export type ShippingListType = {
    shippingAddressBookList: ShippingAddressBookType[];
};

export type ShippingAddressBookType = {
    id: string;
    name: string;
    mallUserId: string;
    recipientName: string;
    postCode: string;
    recipientAddress: string;
    recipientAddressDetail: string;
    recipientPhoneNumber: string;
    isDefaultShippingAddress: boolean;
};

type writeCompletedOrderParameterBody = {
    productList: {
        productGroupId: string;
        representProductId: string;
        representProductName: string;
        representProductImage: string;
        discountMethod: 'percent' | 'won';
        discountValue: number;
        productId: string;
        productOptionsName: string;
        productPrice: number;
        productAmount: number;
        totalProductPrice: number;
        isIssue: boolean;
        ownerType: 'SELLER' | 'VENDOR';
    }[];
    ordererInfo: {
        realName: string;
        email?: string;
        phoneNumber: string;
    };
    paymentInfo: {
        orderProductCount: number;
        shippingCost: number;
        totalPrice: number;
        usedMileage: number;
        usedShoppingPoint: number;
        totalCharge: number;
    };
    cartId?: string;
    shippingAddressInfo: {
        recipientName: string;
        postCode: string;
        recipientAddress: string;
        recipientAddressDetail: string;
        recipientPhoneNumber: string;
        mallRequestMessage?: string;
        shippingRequestMessage?: string;
    };
    mallSalesPolicyConsented: boolean;
    cashReceiptIssued: boolean;
    pgBusinessType: string;
};

type writeCompletedOrderParameterBodyV2 = {
    products: {
        productGroupId: string;
        productId: string;
        productCouponId?: string;
        amount: number;
    }[];
    addonProducts: {
        productGroupId: string;
        productId: string;
        amount: number;
    }[];
    setProducts: {
        productGroupId: string;
        productIds: string[];
        productCouponId?: string;
        amount: number;
    }[];
    ordererInfo: {
        realName: string;
        email?: string;
        phoneNumber: string;
    };
    paymentInfo: {
        orderProductCount: number;
        shippingCost: number;
        totalPrice: number;
        usedMileage: number;
        usedShoppingPoint: number;
        totalCharge: number;
    };
    shippingAddressInfo: {
        recipientName: string;
        postCode: string;
        recipientAddress: string;
        recipientAddressDetail: string;
        recipientPhoneNumber: string;
        mallRequestMessage?: string;
        shippingRequestMessage?: string;
    };
    orderCouponId?: string;
    mallSalesPolicyConsented: boolean;
    ownerType: 'SELLER' | 'VENDOR';
    pgBusinessType: string;
};

export type AdditionalPayResultType = {
    extraChargeInfo: {
        id: string;
        title: string;
        userName: string;
        remark: string;
        paymentStateCode: number;
        extraCharge: number;
        transactionedAt: string;
        paymentInstallmentMonth: number;
        paymentMethod: string;
        cashReceiptIssued: boolean;
        cashReceiptType: string;
        cashReceiptNumber: string;
        cashReceiptUrl: string;
    };
};

export type CreateOrderSheetReturnType = {
    orderInfo: {
        setProductList: {
            representProductId: string;
            discountMethod: 'won' | 'percent';
            discountValue: number;
            productIds: string[];
            productName: string;
            productImage: string;
            productOptionBundle: {
                productId: string;
                optionName: string;
                inventory: number;
            }[];
            productPrice: number;
            productAmount: number;
            totalProductPrice: number;
            ownerType: 'VENDOR' | 'SELLER';
        }[];
        productList: {
            representProductId: string;
            discountMethod: 'won' | 'percent';
            discountValue: number;
            productId: string;
            productName: string;
            productImage: string;
            productOptionsName: string;
            productPrice: number;
            productAmount: number;
            totalProductPrice: number;
            ownerType: 'VENDOR' | 'SELLER';
        }[];
        addonProductList: {
            productGroupId: string;
            productId: string;
            productName: string;
            productImage: string;
            productOptionsName: string;
            productPrice: number;
            productAmount: number;
            totalProductPrice: number;
        }[];
        paymentInfo: {
            totalAmount: number;
            shippingCost: number;
            jejuShippingCost: number;
            islandMountainShippingCost: number;
            totalPrice: number;
            totalCharge: number;
            isShoppingPointAvailable: boolean;
        };
        ownerType: 'VENDOR' | 'SELLER';
    };
};

export type SalesPolicyType = {
    rewardMileageRate: number;
    description: string;
};
export type GroupPurchaseOrderType = {
    mallId: string;
    mallOrderGroupId: string;
    mallOrderGroupNumber: string;
    mallOrderId: string;
    orderedAt: string;
    productInfo: {
        productId: string;
        productOptionsName: string;
        representProductId: string;
        representProductImage: string;
        representProductName: string;
    };
};

export type OnlineCouponListType = {
    issuedAt: string;
    optionName: string;
    orderGroupId: string;
    orderId: string;
    orderNumber: string;
    productGroupId: string;
    productId: string;
    productName: string;
    serialNumber: string;
    url: string;
};

// ProductOptionDetail 타입 정의
export type ProductOptionDetailType = {
    optionValue: string;
    soldOutStatus: boolean;
};

// ProductOption 타입 정의
export type ProductOptionType = {
    optionKey: string;
    optionDetail: ProductOptionDetailType[];
    displayOrder: number;
};

// ProductListItemOption 타입 정의
export type ProductListItemOptionType = {
    optionKey: string;
    optionValue: string;
};

// ProductListItem 타입 정의
export type ProductListItemType = {
    productId: string;
    productCharge: number;
    inventory: number;
    options: ProductListItemOptionType[];
};

// AddonProduct 타입 정의
export type AddonProductType = {
    productId: string;
    productCharge: number;
    inventory: number;
    optionName: string;
    optionValue: string;
    order: number;
};

// ProductOptionsResponseType (최상위 item 객체 타입) 정의
export type ProductOptionsResponseType = {
    productOptionsInfoV2: {
        representProductCharge: number;
        isSetProduct: boolean;
        setRequiredSelectionCount: number;
        optionSelectorType: 'SEPARATED' | 'INTEGRATED';
        productOptions: ProductOptionType[];
        productList: ProductListItemType[];
        addonProductList: AddonProductType[];
    };
};
